import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Mic,
  Square,
  Loader2,
  Keyboard,
  Trash2,
  Plus,
  Zap,
  Clock,
  FileSignature,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import AttachQuoteModal from "@/components/poc/AttachQuoteModal";
import { api, humanizeApiError } from "@/lib/api";

const UNITS = ["m²", "ml", "m³", "unité", "forfait", "kWc", "kWh", "kVA", "h", "j", "kg", "t"];

function formatChrono(ms) {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function formatSec(ms) {
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function PocPage() {
  // Auth (pour CTA "Créer le devis")
  const { state: authState } = useAuth();
  const isAuthed = !!(authState && authState.user);

  // Attach modal (Turn UX-A A.2)
  const [attachOpen, setAttachOpen] = useState(false);

  // Recording state
  const [mode, setMode] = useState("audio"); // "audio" | "text"
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Turn UX-A hotfix TVA : charge le taux TVA par défaut de l'entreprise pour
  // remplir les lignes où le LLM n'a rien tranché (vat_rate === null).
  const [companyDefaultVat, setCompanyDefaultVat] = useState(21);

  useEffect(() => {
    if (!isAuthed) return;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const { data } = await api.get("/company/settings");
        const v = Number(data?.default_vat_rate);
        if ([0, 6, 12, 21].includes(v)) setCompanyDefaultVat(v);
      } catch {
        // silencieux — reste sur 21 par défaut
      }
    })();
  }, [isAuthed]);

  const normalizeLines = (rawLines) =>
    (rawLines || []).map((l) => ({
      ...l,
      // Hotfix Défaut 3 (2026-09-02) — Retrait du helper `resolveVat` frontend.
      // Le backend est désormais l'unique source de vérité TVA. Affichage brut.
      vat_rate: l.vat_rate,
    }));

  // Text mode
  const [freeText, setFreeText] = useState("");

  // Results
  const [transcription, setTranscription] = useState("");
  const [lines, setLines] = useState([]);
  const [rawNotes, setRawNotes] = useState("");
  const [latencies, setLatencies] = useState(null); // { tx, st, total }

  // Item résilience audio (2026-09-21) — Conservation du blob sur échec upload.
  // Sur `catch` de `uploadAudio`, le blob est PRÉSERVÉ dans le state React
  // (`pendingAudioBlob`) au lieu de partir au garbage collector. Permet un
  // retry sans re-dicter. `audioPreviewUrlRef` porte l'ObjectURL pour le
  // bouton "Écouter" ; révoqué au succès, au nouveau enregistrement, ou au
  // démontage du composant.
  const [pendingAudioBlob, setPendingAudioBlob] = useState(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const audioPreviewUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioPreviewUrlRef.current) {
        URL.revokeObjectURL(audioPreviewUrlRef.current);
        audioPreviewUrlRef.current = null;
      }
    };
  }, []);

  const resetResults = () => {
    setTranscription("");
    setLines([]);
    setRawNotes("");
    setLatencies(null);
  };

  const startRecording = async () => {
    resetResults();
    // Item résilience audio — nouvel enregistrement : purge du blob précédent
    // et de son ObjectURL (le user prend une décision explicite : re-dicter).
    if (audioPreviewUrlRef.current) {
      URL.revokeObjectURL(audioPreviewUrlRef.current);
      audioPreviewUrlRef.current = null;
    }
    setPendingAudioBlob(null);
    setUploadFailed(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        // Item résilience audio — stocke le blob AVANT tentative upload pour
        // qu'il survive à un échec réseau (state React persistant tant que
        // le composant est monté). Libéré uniquement sur succès ou nouvelle
        // dictée.
        setPendingAudioBlob(blob);
        await uploadAudio(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setElapsed(0);
      const started = Date.now();
      timerRef.current = setInterval(() => setElapsed(Date.now() - started), 200);
    } catch (err) {
      console.error(err);
      toast.error("Micro refusé", {
        description:
          "Autorise l'accès au micro dans les réglages du navigateur (Safari iOS : Réglages → Safari → Micro).",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  };

  const uploadAudio = async (blob) => {
    if (!blob) return;
    setUploadFailed(false);
    setIsProcessing(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "m4a";
      fd.append("file", blob, `dictation.${ext}`);
      const { data } = await api.post(`/poc/dictate-quote`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscription(data.transcription || "");
      setLines(normalizeLines(data.structured?.lines));
      setRawNotes(data.structured?.raw_notes || "");
      setLatencies({
        tx: data.latency_ms_transcription,
        st: data.latency_ms_structuration,
        total: data.latency_ms_total,
      });
      toast.success("Brouillon de devis généré", {
        description: `${data.structured?.lines?.length || 0} ligne(s) détectée(s)`,
      });
      // Item résilience audio — succès : libère le blob et l'ObjectURL.
      setPendingAudioBlob(null);
      if (audioPreviewUrlRef.current) {
        URL.revokeObjectURL(audioPreviewUrlRef.current);
        audioPreviewUrlRef.current = null;
      }
    } catch (err) {
      // Item résilience audio — échec : conserve blob + affiche bloc inline.
      // console.error laisse la trace debug pour le pro qui aurait accès aux
      // devtools ; aucun détail technique à l'écran.
      console.error("[PocPage] upload failed, blob preserved for retry", err);
      setUploadFailed(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Item résilience audio — 3 handlers du bloc d'erreur inline.
  const retryUploadAudio = () => {
    if (!pendingAudioBlob) return;
    uploadAudio(pendingAudioBlob);
  };
  const playPendingAudio = () => {
    if (!pendingAudioBlob) return;
    if (!audioPreviewUrlRef.current) {
      audioPreviewUrlRef.current = URL.createObjectURL(pendingAudioBlob);
    }
    const audio = new Audio(audioPreviewUrlRef.current);
    audio.play().catch((e) => console.error("[PocPage] audio playback denied", e));
  };
  const switchToTextFromError = () => {
    setMode("text");
    setUploadFailed(false);
    // Le blob reste conservé au cas où l'utilisateur re-bascule sur audio.
  };

  const structureText = async () => {
    if (!freeText.trim()) {
      toast.error("Texte vide");
      return;
    }
    resetResults();
    setIsProcessing(true);
    try {
      const { data } = await api.post(`/poc/structure-quote`, {
        text: freeText.trim(),
      });
      setTranscription(freeText.trim());
      setLines(normalizeLines(data.structured?.lines));
      setRawNotes(data.structured?.raw_notes || "");
      setLatencies({ tx: 0, st: data.latency_ms, total: data.latency_ms });
      toast.success("Structuration terminée", {
        description: `${data.structured?.lines?.length || 0} ligne(s)`,
      });
    } catch (err) {
      // Commit 2 hardening (2026-09-21) — Message métier stable, avec
      // fallback humanizeApiError si le backend renvoie un detail exploitable
      // (page privée, pro connecté → info utile en démo/support).
      const msg = humanizeApiError(err, "Le texte n'a pas pu être traité. Réessayez dans un instant.");
      toast.error("Structuration échouée", { description: msg });
    } finally {
      setIsProcessing(false);
    }
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { description: "", quantity: 1, unit: "unité", unit_price: null, vat_rate: companyDefaultVat },
    ]);

  const totalHT = lines.reduce(
    (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
    0,
  );
  const totalTVA = lines.reduce((acc, l) => {
    const ht = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    return acc + ht * ((Number(l.vat_rate) || 0) / 100);
  }, 0);

  return (
    <div className="text-slate-100">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <header className="mb-10 flex flex-col gap-3 sm:mb-14">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="border-[#C4553B]/40 bg-[#C4553B]/10 text-[#C4553B]"
              data-testid="poc-badge"
            >
              Assistant vocal
            </Badge>
            <Badge variant="outline" className="border-slate-600 bg-slate-800/60 text-slate-300">
              BTP Belgique
            </Badge>
          </div>
          <h1
            className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="poc-title"
          >
            Devis dicté par <span className="text-[#C4553B]">IA</span>
          </h1>
          <p className="max-w-2xl text-base text-slate-400 sm:text-lg">
            Décrivez le chantier à voix haute. Vous repartez avec un brouillon de devis structuré.
          </p>
        </header>

        {/* Mode switch */}
        <div className="mb-8 inline-flex rounded-full border border-slate-700 bg-slate-800/50 p-1">
          <button
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
              mode === "audio"
                ? "bg-[#C4553B] text-white shadow"
                : "text-slate-300 hover:text-white"
            }`}
            onClick={() => setMode("audio")}
            data-testid="mode-audio-btn"
          >
            <Mic className="mr-1.5 inline h-4 w-4" />
            Dictée vocale
          </button>
          <button
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
              mode === "text"
                ? "bg-[#C4553B] text-white shadow"
                : "text-slate-300 hover:text-white"
            }`}
            onClick={() => setMode("text")}
            data-testid="mode-text-btn"
          >
            <Keyboard className="mr-1.5 inline h-4 w-4" />
            Texte libre
          </button>
        </div>

        {/* Capture zone */}
        {mode === "audio" ? (
          <Card className="mb-10 border-slate-700 bg-slate-800/50">
            <CardContent className="flex flex-col items-center gap-6 py-12">
              <div className="relative">
                {isRecording && (
                  <div className="poc-pulse absolute inset-0 rounded-full" aria-hidden />
                )}
                <button
                  disabled={isProcessing}
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`relative z-10 flex h-32 w-32 items-center justify-center rounded-full transition-all duration-200 ${
                    isRecording
                      ? "bg-red-500 shadow-[0_0_40px_rgba(239,68,68,0.4)] hover:bg-red-600"
                      : "bg-[#C4553B] shadow-[0_0_40px_rgba(52,152,219,0.35)] hover:bg-[#A8402C] disabled:opacity-50"
                  }`}
                  data-testid="mic-btn"
                  aria-label={isRecording ? "Arrêter" : "Démarrer l'enregistrement"}
                >
                  {isRecording ? (
                    <Square className="h-10 w-10 fill-white text-white" />
                  ) : isProcessing ? (
                    <Loader2 className="h-10 w-10 animate-spin text-white" />
                  ) : (
                    <Mic className="h-14 w-14 text-white" />
                  )}
                </button>
              </div>

              {isRecording ? (
                <>
                  <div
                    className="font-mono text-2xl font-semibold tabular-nums text-slate-100"
                    data-testid="chrono"
                  >
                    {formatChrono(elapsed)}
                  </div>
                  <div className="flex h-8 items-end gap-1">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <span
                        key={i}
                        className="poc-bar w-1 rounded-full bg-[#C4553B]"
                        style={{
                          height: `${20 + (i % 5) * 6}px`,
                          animationDelay: `${(i * 60) % 800}ms`,
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : isProcessing ? (
                <div className="flex items-center gap-2 text-slate-300" data-testid="processing">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Transcription et analyse en cours…
                </div>
              ) : (
                <p className="text-center text-sm text-slate-400">
                  Touchez le micro et décrivez le chantier à voix haute.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-10 border-slate-700 bg-slate-800/50">
            <CardContent className="flex flex-col gap-4 py-8">
              <label className="text-sm text-slate-400">Décrivez le chantier :</label>
              <Textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Ex : Toiture 120 m², démoussage complet, remplacement de 15 tuiles, réfection des solins de cheminée, accès difficile côté rue, prévoir échafaudage."
                rows={6}
                className="resize-y border-slate-600 bg-slate-900/60 text-slate-100 placeholder:text-slate-500"
                data-testid="free-text-input"
              />
              <div className="flex justify-end">
                <Button
                  disabled={isProcessing || !freeText.trim()}
                  onClick={structureText}
                  className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                  data-testid="structure-text-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Structuration…
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-4 w-4" />
                      Structurer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Item résilience audio (2026-09-21) — Bloc inline d'échec d'upload
            avec blob conservé. Remplace le toast fugitif : le user voit
            l'audio préservé et peut décider (retry / écouter / basculer texte).
            Blob perdu si l'onglet est fermé ou déchargé par iOS (voir doc). */}
        {uploadFailed && pendingAudioBlob && (
          <Card
            className="mb-6 border-red-500/40 bg-red-950/30"
            data-testid="upload-error-card"
          >
            <CardContent className="py-5">
              <div className="flex items-start gap-3">
                <Zap className="mt-0.5 h-5 w-5 shrink-0 text-red-300" />
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <h3
                      className="font-semibold text-red-200"
                      data-testid="upload-error-title"
                    >
                      La dictée n'a pas pu être envoyée
                    </h3>
                    <p className="mt-1 text-sm text-red-100/80">
                      Votre enregistrement est conservé. Réessayez maintenant,
                      ou passez en saisie texte.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={retryUploadAudio}
                      disabled={isProcessing}
                      className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                      data-testid="retry-upload-btn"
                    >
                      {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Réessayer l'envoi
                    </Button>
                    <Button
                      variant="outline"
                      onClick={playPendingAudio}
                      className="border-slate-500 bg-slate-800/60 text-slate-100 hover:bg-slate-700"
                      data-testid="play-pending-btn"
                    >
                      Écouter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={switchToTextFromError}
                      className="border-slate-500 bg-slate-800/60 text-slate-100 hover:bg-slate-700"
                      data-testid="switch-to-text-btn"
                    >
                      Passer en saisie texte
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Latency */}
        {latencies && (
          <div
            className="mb-6 flex flex-wrap items-center gap-3 text-sm text-slate-300"
            data-testid="latency"
          >
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-800/60 px-3 py-1">
              <Clock className="h-3.5 w-3.5 text-[#C4553B]" />
              Transcription : {formatSec(latencies.tx)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-800/60 px-3 py-1">
              <Clock className="h-3.5 w-3.5 text-[#C4553B]" />
              Structuration : {formatSec(latencies.st)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#C4553B]/40 bg-[#C4553B]/10 px-3 py-1 font-medium text-[#C4553B]">
              <Zap className="h-3.5 w-3.5" />
              Total : {formatSec(latencies.total)}
            </span>
          </div>
        )}

        {/* Transcription */}
        {transcription && (
          <section className="mb-8" data-testid="transcription-section">
            <h2 className="mb-2 text-base font-semibold text-slate-200 sm:text-lg">
              Transcription brute
            </h2>
            <p className="rounded-lg border border-slate-700 bg-slate-800/40 p-4 text-sm leading-relaxed text-slate-400">
              {transcription}
            </p>
          </section>
        )}

        {/* Structured lines */}
        {lines.length > 0 && (
          <section className="mb-8" data-testid="quote-lines-section">
            <div className="mb-3 flex items-end justify-between">
              <h2 className="text-base font-semibold text-slate-200 sm:text-lg">
                Brouillon de devis
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/60 text-slate-200 hover:bg-slate-700"
                onClick={addLine}
                data-testid="add-line-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Ligne
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-700 bg-slate-800/40">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-300">Description</TableHead>
                    <TableHead className="w-24 text-slate-300">Qté</TableHead>
                    <TableHead className="w-28 text-slate-300">Unité</TableHead>
                    <TableHead className="w-32 text-slate-300">PU HT (€)</TableHead>
                    <TableHead className="w-24 text-slate-300">TVA</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow
                      key={i}
                      className="border-slate-700 hover:bg-slate-800/30"
                      data-testid={`quote-line-${i}`}
                    >
                      <TableCell>
                        <Input
                          value={l.description}
                          onChange={(e) => updateLine(i, { description: e.target.value })}
                          className="border-slate-600 bg-slate-900/60 text-slate-100"
                          data-testid={`line-desc-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={l.quantity ?? ""}
                          onChange={(e) =>
                            updateLine(i, { quantity: parseFloat(e.target.value) || 0 })
                          }
                          className="border-slate-600 bg-slate-900/60 text-slate-100"
                          data-testid={`line-qty-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={l.unit}
                          onValueChange={(v) => updateLine(i, { unit: v })}
                        >
                          <SelectTrigger
                            className="border-slate-600 bg-slate-900/60 text-slate-100"
                            data-testid={`line-unit-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                            {UNITS.includes(l.unit) ? null : (
                              <SelectItem value={l.unit}>{l.unit}</SelectItem>
                            )}
                            {UNITS.map((u) => (
                              <SelectItem key={u} value={u}>
                                {u}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={l.unit_price ?? ""}
                          placeholder="—"
                          onChange={(e) => {
                            const v = e.target.value;
                            updateLine(i, {
                              unit_price: v === "" ? null : parseFloat(v),
                            });
                          }}
                          className="border-slate-600 bg-slate-900/60 text-slate-100"
                          data-testid={`line-price-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={String(l.vat_rate)}
                          onValueChange={(v) => updateLine(i, { vat_rate: parseInt(v, 10) })}
                        >
                          <SelectTrigger
                            className="border-slate-600 bg-slate-900/60 text-slate-100"
                            data-testid={`line-vat-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                            <SelectItem value="6">6 %</SelectItem>
                            <SelectItem value="21">21 %</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => removeLine(i)}
                          className="rounded p-1 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                          data-testid={`line-remove-${i}`}
                          aria-label="Supprimer la ligne"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex flex-col items-end gap-1 text-sm text-slate-300">
              <div>
                Total HT :{" "}
                <span className="font-mono font-semibold text-slate-100" data-testid="total-ht">
                  {totalHT.toFixed(2)} €
                </span>
              </div>
              <div>
                TVA :{" "}
                <span className="font-mono text-slate-100">{totalTVA.toFixed(2)} €</span>
              </div>
              <div className="text-lg">
                Total TTC :{" "}
                <span className="font-mono font-bold text-[#C4553B]" data-testid="total-ttc">
                  {(totalHT + totalTVA).toFixed(2)} €
                </span>
              </div>
            </div>

            {/* Turn UX-A A.2 — CTA "Créer le devis" (auth requise) */}
            {isAuthed && (
              <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end">
                <Button
                  onClick={() => setAttachOpen(true)}
                  className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                  data-testid="poc-create-quote-btn"
                >
                  <FileSignature className="mr-2 h-4 w-4" />
                  Créer le devis
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Raw notes */}
        {rawNotes && (
          <section className="mb-8" data-testid="raw-notes-section">
            <h2 className="mb-2 text-base font-semibold text-slate-200 sm:text-lg">
              Notes de chantier
            </h2>
            <p className="whitespace-pre-wrap rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm leading-relaxed text-amber-100/90">
              {rawNotes}
            </p>
          </section>
        )}

        <footer className="mt-16 border-t border-slate-800 pt-6 text-xs text-slate-500">
          Brouillon non enregistré — ce mode ne stocke ni l&apos;audio ni la transcription.
        </footer>
      </div>

      {/* Turn UX-A A.2 — Modal rattachement contact/chantier */}
      <AttachQuoteModal
        open={attachOpen}
        onOpenChange={setAttachOpen}
        lines={lines}
        rawNotes={rawNotes}
        transcription={transcription}
      />
    </div>
  );
}
